import { useMemo } from 'react';

import type { UserRole, UserPersona } from '../types/user';

import { useAuth } from './useAuth';

export type Permission =
  // Document permissions
  | 'document:create'
  | 'document:read'
  | 'document:update'
  | 'document:delete'
  | 'document:share'
  | 'document:archive'
  // User permissions
  | 'user:invite'
  | 'user:manage'
  | 'user:deactivate'
  // Tenant permissions
  | 'tenant:read'
  | 'tenant:update'
  | 'tenant:billing'
  // Professional permissions
  | 'professional:access'
  | 'professional:client-view'
  | 'professional:document-access'
  // Admin permissions
  | 'admin:full-access';

const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  owner: [
    'document:create',
    'document:read',
    'document:update',
    'document:delete',
    'document:share',
    'document:archive',
    'user:invite',
    'user:manage',
    'user:deactivate',
    'tenant:read',
    'tenant:update',
    'tenant:billing',
  ],
  admin: [
    'document:create',
    'document:read',
    'document:update',
    'document:delete',
    'document:share',
    'document:archive',
    'user:invite',
    'user:manage',
    'tenant:read',
  ],
  editor: [
    'document:create',
    'document:read',
    'document:update',
    'document:share',
    'document:archive',
  ],
  viewer: ['document:read'],
};

const PERSONA_PERMISSIONS: Partial<Record<UserPersona, Permission[]>> = {
  // BRD-019: Consolidated advisor persona (replaces professional_accountant/intermediary_advisor)
  advisor: [
    'professional:access',
    'professional:client-view',
    'professional:document-access',
  ],
  // Legacy personas (still supported for backwards compatibility)
  professional_accountant: [
    'professional:access',
    'professional:client-view',
    'professional:document-access',
  ],
  intermediary_advisor: [
    'professional:access',
    'professional:client-view',
    'professional:document-access',
  ],
  platform_admin: ['admin:full-access'],
};

interface UsePermissionsReturn {
  permissions: Set<Permission>;
  can: (permission: Permission) => boolean;
  canAny: (permissions: Permission[]) => boolean;
  canAll: (permissions: Permission[]) => boolean;
  hasRole: (role: UserRole) => boolean;
  hasPersona: (persona: UserPersona) => boolean;
}

export function usePermissions(): UsePermissionsReturn {
  const { user } = useAuth();

  const permissions = useMemo(() => {
    const perms = new Set<Permission>();

    if (!user) return perms;

    // Add role-based permissions
    const rolePerms = ROLE_PERMISSIONS[user.role] || [];
    rolePerms.forEach((p) => perms.add(p));

    // Add persona-based permissions
    user.personas.forEach((persona) => {
      const personaPerms = PERSONA_PERMISSIONS[persona] || [];
      personaPerms.forEach((p) => perms.add(p));
    });

    return perms;
  }, [user]);

  const can = (permission: Permission): boolean => {
    return permissions.has(permission);
  };

  const canAny = (perms: Permission[]): boolean => {
    return perms.some((p) => permissions.has(p));
  };

  const canAll = (perms: Permission[]): boolean => {
    return perms.every((p) => permissions.has(p));
  };

  const hasRole = (role: UserRole): boolean => {
    return user?.role === role;
  };

  const hasPersona = (persona: UserPersona): boolean => {
    return user?.personas.includes(persona) ?? false;
  };

  return { permissions, can, canAny, canAll, hasRole, hasPersona };
}
